import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api.js";

interface ActionRow {
  id: string;
  fighter: "a" | "b";
  actionType: string;
  outcome: string | null;
  round: number;
  timeInRoundSec: number;
  createdAt: string;
}

interface FightStats {
  fight_id: string;
  event: { id: string; name: string; date: string };
  weight_class: string | null;
  total_rounds: number;
  current_round: number;
  status: string;
  fighter_a: Record<string, unknown> & { name: string; corner: "red" | "blue" };
  fighter_b: Record<string, unknown> & { name: string; corner: "red" | "blue" };
}

function fmtTime(sec: number) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function HistoryFightPage() {
  const { id } = useParams();
  const [stats, setStats] = useState<FightStats | null>(null);
  const [actions, setActions] = useState<ActionRow[]>([]);

  useEffect(() => {
    if (!id) return;
    api.get<FightStats>(`/api/fights/${id}/stats`).then(setStats);
    api.get<ActionRow[]>(`/api/actions?fightId=${id}`).then(setActions);
  }, [id]);

  if (!stats) return <div className="p-6">Indlæser…</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-bold">
          {stats.fighter_a.name} <span className="text-neutral-500">vs</span> {stats.fighter_b.name}
        </h1>
        <Link to="/history" className="text-sm text-neutral-400 hover:text-neutral-200">
          ← Historik
        </Link>
      </header>

      <div className="text-sm text-neutral-400">
        {stats.event.name} · {stats.weight_class ?? ""}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <StatBlock title={stats.fighter_a.name} stats={stats.fighter_a} />
        <StatBlock title={stats.fighter_b.name} stats={stats.fighter_b} />
      </div>

      <h2 className="font-semibold pt-4">Event log ({actions.length})</h2>
      <div className="bg-neutral-900 rounded-xl border border-neutral-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-800 text-xs uppercase text-neutral-400">
            <tr>
              <th className="text-left p-2">Tid</th>
              <th className="text-left p-2">Runde</th>
              <th className="text-left p-2">R/B</th>
              <th className="text-left p-2">Action</th>
              <th className="text-left p-2">Outcome</th>
            </tr>
          </thead>
          <tbody>
            {actions.map((a) => (
              <tr key={a.id} className="border-t border-neutral-800">
                <td className="p-2 font-mono">{fmtTime(a.timeInRoundSec)}</td>
                <td className="p-2">{a.round}</td>
                <td className="p-2">
                  <CornerBadge fighter={a.fighter === "a" ? stats.fighter_a : stats.fighter_b} />
                </td>
                <td className="p-2">{a.actionType}</td>
                <td className="p-2 text-neutral-400">{a.outcome ?? "—"}</td>
              </tr>
            ))}
            {actions.length === 0 && (
              <tr>
                <td colSpan={5} className="p-4 text-center text-neutral-500">
                  Ingen events registreret.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Actions gemmes med side a/b; loggen viser hjørnet (R/B) som broadcast bruger.
function CornerBadge({ fighter }: { fighter: { name: string; corner: "red" | "blue" } }) {
  const red = fighter.corner === "red";
  return (
    <span
      title={fighter.name}
      className={`inline-block w-6 text-center rounded font-semibold text-xs py-0.5 ${
        red ? "bg-red-700 text-white" : "bg-blue-700 text-white"
      }`}
    >
      {red ? "R" : "B"}
    </span>
  );
}

function StatBlock({ title, stats }: { title: string; stats: Record<string, unknown> }) {
  const rows: [string, string][] = [
    ["Strikes (L/T)", `${stats.total_strikes_landed} / ${stats.total_strikes_thrown}`],
    ["Accuracy", `${stats.total_strikes_accuracy_pct}%`],
    ["Punches", `${stats.punches_landed} / ${stats.punches_thrown}`],
    ["Kicks", `${stats.kicks_landed} / ${stats.kicks_thrown}`],
    ["Takedowns", `${stats.takedowns_landed} / ${stats.takedowns_attempted}`],
    ["Knockdowns", `${stats.knockdowns}`],
    ["Submissions", `${stats.submissions_successful} / ${stats.submissions_attempted}`],
    ["Control time", fmtTime(Number(stats.control_time_sec) || 0)],
  ];
  return (
    <div className="bg-neutral-900 rounded-xl border border-neutral-800 p-4">
      <h3 className="font-semibold mb-2">{title}</h3>
      <dl className="space-y-1">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between text-sm">
            <dt className="text-neutral-400">{k}</dt>
            <dd className="font-mono tabular-nums">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
